import React from 'react';
import Alert from 'components/common/feedback/alert';
import Button from 'components/common/button';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import styled from 'types/theme.types';
import { useGetRunsUsage } from 'stores/rq/environment';
import { useHasPermission } from 'hooks/use-has-permission';
import { getMonthlyRunsResetDate } from 'utils/pay-gate.utils';
import { links } from 'constants/external-links';
import { RUNS_USAGE_WARNING_THRESHOLD } from '@env0/environment-service/consts';

const RunsUsageBanner: React.FunctionComponent = () => {
  const { data } = useGetRunsUsage();
  const { isAuthorized: canManageBilling } = useHasPermission('MANAGE_BILLING');

  const limit = data?.limit ?? null;
  const count = data?.count ?? 0;

  if (limit === null) {
    return null;
  }

  const isReached = count >= limit;
  const isWarning = !isReached && count / limit >= RUNS_USAGE_WARNING_THRESHOLD;

  if (!isReached && !isWarning) {
    return null;
  }

  return (
    <StyledAlert
      data-e2e="runs-usage-banner"
      type={isReached ? 'error' : 'warning'}
      banner
      showIcon
      message={
        <>
          <FormattedMessage
            id={isReached ? 'runs-usage.banner.reached' : 'runs-usage.banner.warning'}
            values={{ count, limit, resetDate: getMonthlyRunsResetDate() }}
          />
          {!canManageBilling && (
            <>
              {' '}
              <FormattedMessage id="runs-usage.banner.contact-admin" />
            </>
          )}
        </>
      }
      action={
        canManageBilling ? (
          <Button
            data-e2e="runs-usage-banner-contact"
            type="primary"
            size="small"
            href={links.BILLING.CONTACT_US}
            isExternalLink>
            <FormattedMessage id="runs-usage.banner.contact-us" />
          </Button>
        ) : undefined
      }
    />
  );
};

// `width` is page layout and stays on both sides; the `!important` border would outrank the bridge.
const StyledAlert = styled(Alert)`
  width: 100%;
`;

// Exported for alert-call-site-parity.spec.
export const RunsUsageBannerAlert = StyledAlert;

export default observer(RunsUsageBanner);
